/**
 * Reformly Free Gifts — Auto-add/remove hidden gift line items for ReformerX products.
 *
 * When any ReformerX product is in the cart, 3 gift variants are added as hidden
 * line items (property _free_gift = 'true'). They are excluded from cart UI via
 * Liquid {% continue %} and from cart counts via JS correction.
 */
(function () {
  'use strict';

  var FREE_GIFT_HANDLES = [
    // ReformerX
    'reformerx-black',
    'reformerx-pink',
    'reformerx-purple',
    'reformerx-black-1',  // product.reformerx-v3 template — native swatches, one handle for all three colours
    // Plans
    'pilates-board-plan-purple',
    'pilates-board-plan-black',
    'pilates-board-plan-pink',
    // Summer Sale
    'pilates-board-purple-summer-sale',
    'pilates-board-black-summer-sale',
    'pilates-board-pink-summer-sale',
    // Basic kits
    'pilates-basic-kit',
    'pilates-basic-kit-1',
    'pilates-basic-kit-2',
    // Essential / Foundations
    'pilates-foundations-kit-1',
    // Starter Sculpt
    'starter-sculpt-set',
    'starter-sculpt-set-1',
    'starter-sculpt-set-2',
    // Total Tone
    'total-tone-set',
    'total-tone-set-1',
    'total-tone-set-2',
    // Power Set
    'pilates-power-set',
    'pilates-power-set-1',
    'pilates-power-set-2',
    // Complete Studio
    'complete-studio-kit',
    'complete-studio-set',
    'complete-studio-set-1',
    /* V4 kits, created 2026-10-06. Same gifts as the 2025 kits they replace —
       without these the new generation silently stops qualifying, and both
       generations are live side by side right now.

         pilates-basic-kit-v2          <- pilates-basic-kit
         starter-sculpt-kit-v2         <- starter-sculpt-set
         reformly-board-essentials-kit <- pilates-power-set
         reformly-board-pro-kit        <- complete-studio-kit
         reformly-board-basic-kit      new combination, no 2025 counterpart
         pilates-starter-kit           new, mat + bands only

       pilates-starter-kit carries no board, which follows the existing rule
       rather than breaking it: pilates-basic-kit above is also boardless and
       has always qualified, so the gifts attach to the kit, not the board.

       KNOWN: handleCartChange() runs on `liquid-ajax-cart:init` and `pageshow`,
       not on add-to-cart, so a cart that already holds one of these reconciles
       its gifts on the next page load and that `add` opens the drawer. Not new
       — every handle in this list behaves that way — but it is why a cart left
       holding a V4 kit pops the drawer on load. */
    'pilates-basic-kit-v2',
    'starter-sculpt-kit-v2',
    'reformly-board-basic-kit',
    'reformly-board-essentials-kit',
    'reformly-board-pro-kit',
    'pilates-starter-kit',
    /* pilates-essential-kit — $99, product.pilates-kit-v2, one variant
       (Purple Passion). It is a COMPONENT of the Pro and Power kits, which
       qualify through their own handles, but on its own it earned nothing
       until its V4 page shipped advertising four gifts (2026-10-13). */
    'pilates-essential-kit',
    // OT001 landing page
    'reformly-pilates-board-1',
    // OT002 landing page
    'reformly-pilates-board-2',
    // OT003 landing page
    'reformly-pilates-board-3',
    // OT004 landing page
    'reformly-pilates-board-4',
    // OT005 landing page
    'reformly-pilates-board-5',
    /* OT006 landing page. Takes the SHARED set (GIFT_VARIANT_IDS), unlike OT005
       which replaces it — OT006's own bundle items (Pilates Mat, Weight Bands,
       Grip Socks) are added by the page itself, without the _free_gift property,
       so this file never sees them and the two sets do not collide. */
    'reformly-pilates-board-6'
  ];

  var GIFT_VARIANT_IDS = [
    45947079491641,
    45947083849785,
    45947083096121
  ];

  // Bundle gifts — split by board group so each removes only its own membership + socks
  var OLD_SOCKS_VARIANT_ID = 45924805967929;
  var SUMMER_SOCKS_VARIANT_ID = 45970505039929;
  // Reformly Plus bundle also ships a nutrition plan + express shipping gift (30-day / discounted)
  var R30_NUTRITION_VARIANT_ID = 46239945293881;
  var R30_SHIPPING_VARIANT_ID = 46239947489337;

  var OLD_BOARD_HANDLES = [
    'pilates-board-plan-purple',
    'pilates-board-plan-black',
    'pilates-board-plan-pink'
  ];
  var OLD_MEMBERSHIP_VARIANT_ID = 45924536549433;

  var SUMMER_BOARD_HANDLES = [
    'pilates-board-purple-summer-sale',
    'pilates-board-black-summer-sale',
    'pilates-board-pink-summer-sale'
  ];
  var SUMMER_MEMBERSHIP_VARIANT_ID = 45924536549433;

  var JULY4_YEARLY_BOARD_HANDLES = [
    'pilates-board-purple-4th-july-sale',
    'pilates-board-black-4th-july-sale-yearly',
    'pilates-board-pink-4th-july-sale-yearly'
  ];
  var JULY4_MONTHLY_BOARD_HANDLES = [
    'pilates-board-purple-4th-july-sale-monthly',
    'pilates-board-black-4th-july-sale-monthly',
    'pilates-board-pink-4th-july-sale-monthly'
  ];
  var BONUS_BOARD_HANDLES = [
    'pilates-board-bonus'
  ];
  var R30_BOARD_HANDLES = [
    'reformly-board-30-day-plan',
    'reformly-board-30-day-plan-discounted',
    'reformly-board-30-day-plan-quiz-offer',  // onboarding option 2 (bundled with the 1-month membership)
    'reformly-pilates-board'                  // 005/006/007 — carries either membership
  ];
  // 90-day boards — bundled with the 90-day-toned-transformation-plan membership
  var R90_BOARD_HANDLES = [
    'reformly-board-90-day-plan',
    'reformly-board-90-day-plan-quiz-offer',  // onboarding option 1
    'reformly-pilates-board'                  // 005/006/007 — carries either membership
  ];

  // OT001 landing page — the board carries one of three Transformation Program
  // subscriptions, added as a second line item at add-to-cart.
  //
  // Paired at VARIANT level, not handle level. A handle-level check counts every
  // board variant together, so a cart holding a 28-Day board and a 90-Day board
  // keeps both programs alive when either board is removed — the orphaned
  // subscription survives and is still charged. Each program is tied to exactly
  // the three colour variants of its own plan.
  // The generic gift set ships the 60-Day digital product. On OT001 the gift
  // must match the plan the buyer chose, so the 60-Day gift variant is swapped
  // for the plan's own. 60-Day keeps the shared one.
  // On OT001 the program is SOLD as a subscription line item, so shipping the
  // 60-Day Program digital product as a free gift would duplicate what the buyer
  // just paid for. Dropped from the gift set whenever an OT001 board is present;
  // the other gifts (14-Day Kickstart, 100+ Workouts) still apply.
  var OT001_EXCLUDED_GIFT_ID = 45947083849785; // 60-Day Program (Digital Product)

  /* Two different gates, deliberately separate.

     OT006_PLAN_BOARDS — the six "With Plan" variants, either bundle. These earn
     the shared gift set, and drop the 60-Day digital product from it because the
     page sells that programme as a paid subscription line.

     OT006_BUNDLE_BOARDS — all six "Board Bundle" variants, BOTH plans. The Mat /
     Weight Bands / Grip Socks are what make it a bundle, so the toggle does not
     gate them; the page adds them itself and this list keeps the legacy socks
     sweep from deleting them and drives their removal with the board. */
  var OT006_PLAN_BOARDS = [
    46458154844217, 46458154909753, 46458154975289,  // Board / With Plan
    46458132987961, 46458133053497, 46458133119033   // Board Bundle / With Plan
  ];
  var OT006_BUNDLE_BOARDS = [
    46458132987961, 46458133053497, 46458133119033,  // Board Bundle / With Plan
    46458133020729, 46458133086265, 46458133151801   // Board Bundle / One-Time
  ];

  /* The two recurring programmes the offer sells alongside the board. Removed
     with it: an orphaned subscription here is not cosmetic, it bills forever.
     Safe to key on variant id alone — no other page sells these two. */
  var OT006_PROGRAM_VARIANTS = [
    46459915206713, // 60-Day Transformation Program  (Board,        plan 6871351353)
    46459983921209  // 90-Day Transformation Program  (Board Bundle, plan 6871384121)
  ];

  // OT002's boards. They sell the same 60-Day program as OT001's 60-Day plan, so
  // they share its pairing entry below and get the same treatment throughout:
  // the program is a paid subscription line, so the free 60-Day digital product
  // is dropped from the gift set rather than duplicating what was just bought.
  var OT002_BOARDS = [46392279924793, 46392279957561, 46392279990329];

  var OT001_PLANS = [
    {
      plan: '28-Day',
      program: 46390418833465,
      boards: [46390404775993, 46390404808761, 46390404841529] // Lavender, Black, Pink
    },
    {
      plan: '60-Day',
      program: 46390421684281,
      // OT002 sells the same 60-Day program against its own board, so its three
      // colour variants join this entry rather than getting one of their own —
      // two entries pointing at one program variant would fight over its
      // quantity when a cart holds both boards.
      boards: [46390405890105, 46390405955641, 46390406021177].concat(OT002_BOARDS)
    },
    {
      plan: '90-Day',
      program: 46390424698937,
      boards: [46390405922873, 46390405988409, 46390406053945]
    },
    /* OT003 (reformly-pilates-board-3). Its own INTRO program products, priced
       at the discounted first payment — a Seal product swap converts each to
       the full-price product before the 2nd payment, so the ids below are only
       ever what the storefront adds. The One Time Purchase variants
       (46394757742649 / 46394757840953 / 46394757939257) are absent on purpose:
       that plan sells the board alone, so there is no program to pair. */
    {
      plan: 'OT003 90-Day',
      program: 46394724679737,
      boards: [46394757808185, 46394757906489, 46394758004793] // Black, Lavender, Pink
    },
    {
      plan: 'OT003 60-Day',
      program: 46394724417593,
      boards: [46394757775417, 46394757873721, 46394757972025]
    },
    /* OT004 (reformly-pilates-board-4). One plan only — the 28-Day program, on
       its own subscription product (28-day-transformation-program-1, $27, sold
       with the first cycle free). Its One Time Purchase variants
       (46403501785145 / 46403501883449 / 46403501981753) are absent on purpose:
       that plan adds the board alone, so there is no subscription to pair and
       nothing to strip when it goes. */
    {
      plan: 'OT004 28-Day',
      program: 46403509616697,
      boards: [46403501752377, 46403501850681, 46403501948985] // Black, Lavender, Pink
    }
  ];

  var JULY4_YEARLY_MEMBERSHIP_VARIANT_ID = 46133255798841;
  var JULY4_MONTHLY_MEMBERSHIP_VARIANT_ID = 46181559992377;
  // 30-day-plan-bundle subscription — bundled with the R30 boards only
  var R30_MEMBERSHIP_VARIANT_ID = 46277813403705;
  // 90-day-toned-transformation-plan subscription — bundled with the R90 boards only
  var R90_MEMBERSHIP_VARIANT_ID = 46250514874425;
  /* OT005's board attaches ONE of these two Reformly+ subscriptions, chosen by
     the offer card's annual/monthly toggle. They are recurring charges, so they
     must not outlive their board — an orphan here bills the customer forever.
     Both are swept on the OT005 board leaving, and only that board: neither id
     appears in any other bundle, so no other page can be affected. */
  var OT005_MEMBERSHIP_VARIANT_IDS = [
    46434238398521, // Annual Reformly+ ($99.00/yr, plan 6783795257)
    46434230698041  // Flexible Reformly+ Plan ($19.99/4wk, plan 6747652153)
  ];

  var busy = false; // prevents re-entrant triggers

  /* ── helpers ─────────────────────────────────────────────── */

  function isHiddenItem(item) {
    return (
      (item.properties && item.properties._free_gift) ||
      GIFT_VARIANT_IDS.indexOf(item.variant_id) !== -1
    );
  }

  function getVisibleCount(cart) {
    var count = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (!isHiddenItem(cart.items[i])) {
        count += cart.items[i].quantity;
      }
    }
    return count;
  }

  /* OT003's One Time Purchase variants. The gift set is granted by HANDLE, and
     all nine OT003 board variants share one handle — so without this list the
     one-time buyer would collect the four gifts alongside the two subscription
     plans. Excluded from the trigger count instead of the handle list, so the
     90-Day and 60-Day variants on the same product still qualify. */
  var OT003_ONE_TIME_BOARDS = [
    46394757742649, 46394757840953, 46394757939257,
    // OT004's One Time Purchase colours. Same reasoning: all six OT004 variants
    // share the handle reformly-pilates-board-4, which is what grants the gift
    // set, so without these the one-time buyer would collect the gifts that pay
    // for the subscription plan they did not take.
    46403501785145, 46403501883449, 46403501981753,
    // OT005's One Time Purchase colours, for the same reason — all six board-5
    // variants share the handle reformly-pilates-board-5.
    46430866407481, 46430866473017, 46430866538553,
    /* OT006 contributes SIX: its twelve variants all share the handle
       reformly-pilates-board-6, and the shared gift set is earned by the PLAN,
       not by the bundle — both "Board" and "Board Bundle" qualify as long as
       "With Plan" is chosen. Only the six "One-Time" variants are excluded.

       The bundle items (Mat, Weight Bands, Grip Socks) are a separate thing and
       ARE Board-Bundle-only; they are added by the page itself and gated by
       OT006_BUNDLE_BOARDS below, not here. */
    46458154876985, 46458154942521, 46458155008057,  // Board / One-Time
    46458133020729, 46458133086265, 46458133151801   // Board Bundle / One-Time
  ];

  /* Boards whose buyer PAYS for a transformation programme, and therefore has
     the 60-Day digital product stripped from the shared gift set. The set swaps
     in the Personalized Nutrition Plan instead — a gift they aren't otherwise
     buying — so they still receive three gifts rather than two.

     One-time boards are absent throughout: that plan earns no gifts at all.

     Grouped per page because the two do NOT grant the same product. There are
     two Personalized Nutrition Plans in the store, and OT004 uses the newer
     one. Repointing a single shared id would have moved OT003's gift too. */
  var NUTRITION_GIFT_SETS = [
    {
      // OT003 (reformly-pilates-board-3)
      boards: [
        46394757808185, 46394757906489, 46394758004793, // 90-Day: Black, Lavender, Pink
        46394757775417, 46394757873721, 46394757972025  // 60-Day
      ],
      // personalized-nutrition-plan
      gifts: [46239945293881]
    },
    {
      // OT004 (reformly-pilates-board-4) — one plan, the 28-Day
      boards: [46403501752377, 46403501850681, 46403501948985], // Black, Lavender, Pink
      gifts: [
        46367471304761, // personalized-nutrition-plan-1 (product 8387818782777)
        45970505039929  // pilates-grip-socks-1 — the third tile on the card
      ]
    }
  ];

  // Flat list, for the callers that only need "is one of these boards present".
  var NUTRITION_GIFT_BOARDS = NUTRITION_GIFT_SETS.reduce(function (all, set) {
    return all.concat(set.boards);
  }, []);

  // OT004's own boards, kept separate from the flat list above because the
  // socks removal rule has to spare THIS page without also sparing OT003's.
  var OT004_GIFT_BOARDS = NUTRITION_GIFT_SETS[1].boards;

  function hasOt004GiftBoard(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (OT004_GIFT_BOARDS.indexOf(cart.items[i].variant_id) !== -1) return true;
    }
    return false;
  }

  // The swap gifts THIS cart earns, or an empty list.
  function swapGiftsFor(cart) {
    for (var s = 0; s < NUTRITION_GIFT_SETS.length; s++) {
      for (var i = 0; i < cart.items.length; i++) {
        if (NUTRITION_GIFT_SETS[s].boards.indexOf(cart.items[i].variant_id) !== -1) {
          return NUTRITION_GIFT_SETS[s].gifts;
        }
      }
    }
    return [];
  }

  function hasNutritionGiftBoard(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (NUTRITION_GIFT_BOARDS.indexOf(cart.items[i].variant_id) !== -1) return true;
    }
    return false;
  }

  function getReformerXQty(cart) {
    var qty = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (OT003_ONE_TIME_BOARDS.indexOf(item.variant_id) !== -1) continue;
      if (FREE_GIFT_HANDLES.indexOf(item.handle) !== -1) {
        qty += item.quantity;
      }
    }
    return qty;
  }

  /* OT005 does not take the shared gift set at all — it REPLACES it with its own
     six, which are what the offer block's "Included" row advertises. Four are
     OT005-only products; nutrition and socks are the existing shared ones.

     Keyed to the 28-Day board variants only. The One Time Purchase colours are
     in OT003_ONE_TIME_BOARDS above, so that plan earns nothing. */
  var OT005_BOARDS = [46430866374713, 46430866440249, 46430866505785];

  var OT005_GIFT_IDS = [
    46433853734969, // 30-day-plan-digital-product   $99
    46367471304761, // personalized-nutrition-plan-1 $20  (shared)
    46433855209529, // unlimited-classes             $30
    46433855963193, // progress-tracking             $10
    45970505039929  // pilates-grip-socks-1          $19.99 (shared)
  ];
  /* Published so OT005's ATC can post the gifts in the SAME /cart/add.js call as
     the board, instead of waiting for this file's next sync to add them in a
     second request. That gap is what leaves a cart briefly holding a board with
     no gifts — harmless while OT005 opens the drawer, fatal if it ever redirects
     straight to checkout (the app-membership02 bug). This list stays the one
     source of truth; the sync below still reconciles, and finds nothing missing. */
  window.reformlyOt005GiftIds = OT005_GIFT_IDS;


  function hasOt005Board(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (OT005_BOARDS.indexOf(cart.items[i].variant_id) !== -1) return true;
    }
    return false;
  }

  /* BOARDLESS KIT pages — product.basic-kit-v2 and product.pilates-kit-v2.
     Those pages advertise the 30-Day Transformation Program where the board
     pages advertise the 60-Day, and the gift set follows the page.

     BOARD + KIT BUNDLES ARE DELIBERATELY ABSENT. An earlier version of this
     list (commits fc2d672b / a4b2180e, reverted by 13515261) also carried the
     ten product.complete-studio handles. Those pages keep the shared set — if
     a board is in the bundle, the bundle promises what the board promises.

     By HANDLE, not variant id: these products are already in
     FREE_GIFT_HANDLES, so this adds no new trigger. It only changes which
     three variants that trigger grants. A variant-level list would be dozens
     of ids kept in step with an admin that can add a colour.

     pilates-essential-kit renders product.pilates-kit-v2 alongside
     pilates-basic-kit-v2; pilates-starter-kit renders product.basic-kit-v2
     alongside the basic kit. All four are listed so no product is sold on a
     page whose promise its cart does not keep. */
  var KIT_GIFT_HANDLES = [
    'pilates-basic-kit-v2',
    'pilates-starter-kit',
    'pilates-essential-kit'
  ];

  /* 30-day-transformation-program-digital-product, product 10372795826233.
     One variant, so every kit and every colour grants the same line.

     DISTINCT from OT001_EXCLUDED_GIFT_ID (45947083849785, the programme slot
     this filters out), which is what makes the substitution real work rather
     than a relabel. Two earlier ids are dead: 50757820481593 (product
     10370210857017) from the first attempt, and 45947083849785 itself, which
     briefly owned this handle and is still what the shared set grants as the
     "60-Day Program". */
  var KIT_PROGRAM_GIFT_ID = 50768361652281;

  function hasKit(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      if (KIT_GIFT_HANDLES.indexOf(cart.items[i].handle) !== -1) return true;
    }
    return false;
  }

  /* A qualifying product that is NOT one of the boardless kits — i.e. a board.

     Derived from the two lists rather than a third hardcoded one: every gift
     trigger is in FREE_GIFT_HANDLES, and the boardless kits are exactly
     KIT_GIFT_HANDLES, so the difference is the boards. A new board colour added
     to FREE_GIFT_HANDLES is therefore picked up here with no second edit — the
     failure mode this file already has twice over with its mirrored lists.

     OT003's one-time variants are excluded for the same reason getReformerXQty
     excludes them: they share a handle with the plans but earn no gifts. */
  function hasBoard(cart) {
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (OT003_ONE_TIME_BOARDS.indexOf(item.variant_id) !== -1) continue;
      if (KIT_GIFT_HANDLES.indexOf(item.handle) !== -1) continue;
      if (FREE_GIFT_HANDLES.indexOf(item.handle) !== -1) return true;
    }
    return false;
  }

  /* Published so the hero can ask "is the product on THIS page a boardless
     kit?" before choosing which gift list to post.

     It cannot infer that from reformlyKitGiftIds existing: this file runs on
     the board PDP and the board+kit bundle too, so the list is defined on
     every one of those pages. Only the handle distinguishes them. */
  window.reformlyKitGiftHandles = KIT_GIFT_HANDLES;

  /* The kit set, published so the kit PDP's own ATC can post these in the SAME
     /cart/add.js as the kit — the pattern window.reformlyOt005GiftIds uses
     below, and for the same reason.

     Without it the hero reads window.rypCart.giftIds (reformly-plus-cart.js),
     which is the shared ReformerX trio and knows nothing about the swap. The
     kit's add would post the 60-Day, this file would then find it stale,
     remove it and add the 30-Day: two extra requests, and a drawer that can
     show the wrong programme before the swap lands.

     De-duplicated, which the earlier version did not need to be: with the
     programme slot and KIT_PROGRAM_GIFT_ID currently the same id, a plain
     concat would post that variant twice in one add. */
  window.reformlyKitGiftIds = GIFT_VARIANT_IDS
    .filter(function (id) { return id !== OT001_EXCLUDED_GIFT_ID; })
    .concat([KIT_PROGRAM_GIFT_ID])
    .filter(function (id, i, all) { return all.indexOf(id) === i; });

  // GIFT_VARIANT_IDS with the OT001 substitution applied. Returns the shared
  // list untouched when no OT001 board is present, so every other page keeps its
  // existing behaviour.
  function effectiveGiftIds(cart) {
    // Checked first and returned whole: OT005 substitutes the entire set, so it
    // must not fall through to the OT001 filtering below.
    if (hasOt005Board(cart)) return OT005_GIFT_IDS;

    /* BOARD + KIT IS A UNION, NOT A CHOICE.

       Each product brings its own programme: the board grants the 60-Day, the
       kit the 30-Day. They are not alternatives when both are in the cart —
       the buyer paid for both products, so both programmes are owed.

       The swap in reformlyKitGiftIds exists only because a BOARDLESS kit
       cannot promise the 60-Day; it replaces the slot rather than adding to it.
       Applying that replacement while a board is present was the bug: it made
       the 60-Day stale, so removing the kit removed the BOARD's programme and
       left the kit's behind — the reported "remove the kit, lose the 60-Day".

       Returning the union instead means nothing is stale while both are in the
       cart, and when the kit goes the 30-Day becomes stale on its own and is
       the only line swept. The board keeps its 60-Day.

       Returned whole, like the OT005 branch above, so it does not fall into
       the OT001 filtering below — a cart holding a kit and an OT001 board gets
       this set rather than two substitutions fighting over one slot. */
    if (hasKit(cart)) {
      if (hasBoard(cart)) {
        return GIFT_VARIANT_IDS.concat([KIT_PROGRAM_GIFT_ID]);
      }
      return window.reformlyKitGiftIds;
    }


    /* OT006 sells its programme as a PAID subscription line — the 60-Day or
       90-Day Transformation Program the offer block adds alongside the board —
       so shipping the free 60-Day Program digital product on top would hand the
       buyer a second copy of what they just paid for. Same reasoning as OT001
       and OT002 below; the other two gifts still apply. */
    var hasOt006Board = false;
    for (var q = 0; q < cart.items.length; q++) {
      if (OT006_PLAN_BOARDS.indexOf(cart.items[q].variant_id) !== -1) {
        hasOt006Board = true;
        break;
      }
    }

    var hasOt001Board = false;
    for (var p = 0; p < OT001_PLANS.length && !hasOt001Board; p++) {
      for (var i = 0; i < cart.items.length; i++) {
        var vid = cart.items[i].variant_id;
        if (OT001_PLANS[p].boards.indexOf(vid) !== -1) {
          hasOt001Board = true;
          break;
        }
      }
    }

    /* OT006 drops the 60-Day digital product and stops there — it takes no
       swap-in replacement, unlike OT003/OT004 below, so its buyers get the two
       remaining shared gifts plus the three bundle items the page adds itself. */
    if (hasOt006Board && !hasOt001Board) {
      return GIFT_VARIANT_IDS.filter(function (id) {
        return id !== OT001_EXCLUDED_GIFT_ID;
      });
    }

    if (!hasOt001Board) return GIFT_VARIANT_IDS;

    var ids = GIFT_VARIANT_IDS.filter(function (id) {
      return id !== OT001_EXCLUDED_GIFT_ID;
    });

    // OT003 and OT004 replace the dropped 60-Day digital product rather than
    // just losing it, so their buyers still receive three gifts. OT001 and
    // OT002 carts get the list unchanged.
    swapGiftsFor(cart).forEach(function (gid) {
      if (ids.indexOf(gid) === -1) ids = ids.concat([gid]);
    });

    return ids;
  }

  // Gift line items that no longer belong. Covers the OT001 case: adding that
  // board must clear the 60-Day Program gift if an earlier add already put it in
  // the cart.
  function getStaleGiftItems(cart) {
    var wanted = effectiveGiftIds(cart);
    var stale = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (!item.properties || !item.properties._free_gift) continue;
      if (wanted.indexOf(item.variant_id) === -1) stale.push(item);
    }
    return stale;
  }

  function getGiftItems(cart) {
    // Against the EFFECTIVE list, not the static one — otherwise a swapped
    // OT001 gift is not recognised as a gift, so it never gets its quantity
    // synced and never gets removed with the rest.
    var wanted = effectiveGiftIds(cart);
    var gifts = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (
        item.properties &&
        item.properties._free_gift &&
        wanted.indexOf(item.variant_id) !== -1
      ) {
        gifts.push(item);
      }
    }
    return gifts;
  }

  function getMissingGiftIds(cart) {
    var wanted = effectiveGiftIds(cart);
    var missing = [];
    for (var g = 0; g < wanted.length; g++) {
      var found = false;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (
          item.variant_id === wanted[g] &&
          item.properties &&
          item.properties._free_gift
        ) {
          found = true;
          break;
        }
      }
      if (!found) missing.push(wanted[g]);
    }
    return missing;
  }

  /* ── DOM count correction ───────────────────────────────── */

  function updateDomCounts(cart) {
    var count = getVisibleCount(cart);

    /* Header badge(s): .cart-count-bubble > span[aria-hidden]

       The badge is rendered by header.liquid ONLY when the cart already has
       items ({%- if _hdr_visible_count > 0 -%}), so on a page loaded with an
       empty cart the element does not exist at all. Updating it was therefore a
       no-op after the first add, and the count only appeared on the next page
       load — the reported nav-count desync.

       Created here to match that markup exactly, so a later server render
       replaces it with an identical node. */
    var badges = document.querySelectorAll('.cart-count-bubble');
    if (!badges.length && count > 0) {
      var iconLink = document.getElementById('cart-icon-bubble');
      if (iconLink) {
        var bubble = document.createElement('div');
        bubble.className = 'cart-count-bubble';
        var n = document.createElement('span');
        n.setAttribute('aria-hidden', 'true');
        n.textContent = count;
        bubble.appendChild(n);
        var sr = document.createElement('span');
        sr.className = 'visually-hidden';
        sr.textContent = count + ' items';
        bubble.appendChild(sr);
        iconLink.appendChild(bubble);
        badges = document.querySelectorAll('.cart-count-bubble');
      }
    }
    for (var i = 0; i < badges.length; i++) {
      var span = badges[i].querySelector('span[aria-hidden]');
      if (span) span.textContent = count;
      badges[i].style.display = count > 0 ? '' : 'none';
    }

    // Drawer header: #ajax-cart-quantity
    var drawerQty = document.getElementById('ajax-cart-quantity');
    if (drawerQty) drawerQty.textContent = count;
  }

  /* ── cart mutations (via liquid-ajax-cart API) ───────────── */

  function addGifts(missing, qty, callback) {
    var items = [];
    for (var i = 0; i < missing.length; i++) {
      items.push({
        id: missing[i],
        quantity: qty,
        properties: { _free_gift: 'true' }
      });
    }

    if (window.liquidAjaxCart && window.liquidAjaxCart.add) {
      /* Identity of the cached cart BEFORE the add. The library swaps the whole
         object when it refreshes (`N = R, R = i`), so a changed reference means
         the add already updated the state and the follow-up below is not
         needed. On the GemPages layouts the maison drawer is still registered
         as a cart section, so adds there DO refresh — and an extra update()
         would make them re-render that 160 KB drawer a second time. */
      var cartBefore = window.liquidAjaxCart.cart;

      window.liquidAjaxCart.add(
        { items: items },
        {
          /* Refresh liquid-ajax-cart's state BEFORE handing control back.

             The library updates window.liquidAjaxCart.cart only from a response
             body that carries `token`:

               (extra?.ok && extra.body.token) ? i = extra.body
                 : (res?.ok && res.body.token) && (i = res.body);
               i && (R = i)

             /cart/add.js returns the added line items — no `token` — so an add
             refreshes the state only through `extra`, the follow-up sections
             request. That request is made only when the page contains
             [data-ajax-cart-section] elements, and the only file carrying that
             attribute is sections/maison-cart-drawer.liquid, which no longer
             renders outside the GemPages layouts.

             So on every normal page an LAC add leaves the cached cart exactly
             as it was. The caller below is handleCartChange(), which re-reads
             that cache, still sees these gifts missing, and adds them again —
             and /cart/add.js merges rather than failing, so each pass is +1 on
             every gift line. Measured on a local dev server: a cart holding a
             qualifying kit with no gifts reached quantity 2 on one page load
             and 5 by the next. An order on 2026-10-12 shipped at 109.

             /cart/update.js DOES return the cart with a token, so this empty
             update is what makes the re-check see reality. Skipped entirely
             when the add already refreshed the state, so the pages that were
             never broken pay nothing. */
          lastCallback: function () {
            if (window.liquidAjaxCart.cart !== cartBefore) {
              /* Already refreshed — a cart section was registered on this page
                 and the library's follow-up request carried the new state. */
              if (callback) callback();
            } else if (window.liquidAjaxCart.update) {
              window.liquidAjaxCart.update({}, {
                lastCallback: function () { if (callback) callback(); }
              });
            } else if (callback) {
              callback();
            }
          }
        }
      );
    } else {
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function () {
          /* Fallback branch only — the liquidAjaxCart path above is observed by
             theme.liquid, this one emits nothing. See layout/theme.liquid. */
          if (window.rfStampCart) rfStampCart();
          if (callback) callback();
        })
        .catch(function () { if (callback) callback(); });
    }
  }

  /* Line-precise quantity update.

     updateGiftQty below keys `updates` by VARIANT ID, which cannot address one
     line when two share a variant — and OT006's gift lines do exactly that: the
     colourless Grip Socks appears once per bundle, same variant, distinguished
     only by its _ot006_gift parent. Zeroing by id removed BOTH socks lines when
     one board was taken out.

     The positional array form of /cart/update.js addresses lines by index, so
     each one can be changed on its own. Kept separate rather than folded into
     updateGiftQty: every other offer pairs one line per variant and works fine
     on the id form, and this is not the file to refactor speculatively.

     `lines` are items taken from THIS cart object, so identity comparison is
     what pairs them back to their index. */
  /* Same positional-array update, but one target per line — two gift lines can
     need different quantities when their parent boards do. */
  function updateLinesQtyEach(cart, lines, targets, callback) {
    var quantities = [];
    for (var i = 0; i < cart.items.length; i++) {
      var target = cart.items[i].quantity;
      for (var l = 0; l < lines.length; l++) {
        if (lines[l] === cart.items[i]) { target = targets[l]; break; }
      }
      quantities.push(target);
    }

    if (window.liquidAjaxCart && window.liquidAjaxCart.update) {
      window.liquidAjaxCart.update(
        { updates: quantities },
        { lastCallback: function () { if (callback) callback(); } }
      );
    } else {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: quantities })
      })
        .then(function () { if (callback) callback(); })
        .catch(function () { if (callback) callback(); });
    }
  }

  function updateLinesQty(cart, lines, targetQty, callback) {
    var quantities = [];
    for (var i = 0; i < cart.items.length; i++) {
      var target = cart.items[i].quantity;
      for (var l = 0; l < lines.length; l++) {
        if (lines[l] === cart.items[i]) { target = targetQty; break; }
      }
      quantities.push(target);
    }

    if (window.liquidAjaxCart && window.liquidAjaxCart.update) {
      window.liquidAjaxCart.update(
        { updates: quantities },
        { lastCallback: function () { if (callback) callback(); } }
      );
    } else {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: quantities })
      })
        .then(function () { if (callback) callback(); })
        .catch(function () { if (callback) callback(); });
    }
  }

  function updateGiftQty(giftItems, targetQty, callback) {
    var updates = {};
    for (var i = 0; i < giftItems.length; i++) {
      updates[String(giftItems[i].variant_id)] = targetQty;
    }

    if (window.liquidAjaxCart && window.liquidAjaxCart.update) {
      window.liquidAjaxCart.update(
        { updates: updates },
        {
          lastCallback: function () {
            if (callback) callback();
          }
        }
      );
    } else {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function () { if (callback) callback(); })
        .catch(function () { if (callback) callback(); });
    }
  }

  /* ── main handler (runs after every cart mutation) ──────── */

  // Keep each program's quantity equal to its own plan's board quantity.
  //
  // Presence alone is not enough: adding the same board twice merges into one
  // line at qty 2 and the program follows, so dropping the board to 1 would
  // leave 2 subscriptions billing. Zero board qty means zero program, which also
  // covers removal.
  function syncOt001ProgramQty(cart, callback) {
    var updates = {};
    var changed = false;

    for (var p = 0; p < OT001_PLANS.length; p++) {
      var plan = OT001_PLANS[p];

      var boardQty = 0;
      var programLine = null;
      for (var i = 0; i < cart.items.length; i++) {
        var item = cart.items[i];
        if (plan.boards.indexOf(item.variant_id) !== -1) boardQty += item.quantity;
        if (item.variant_id === plan.program) programLine = item;
      }

      if (!programLine) continue;
      if (programLine.quantity === boardQty) continue;

      updates[String(plan.program)] = boardQty;
      changed = true;
    }

    if (!changed) return false;

    if (window.liquidAjaxCart && window.liquidAjaxCart.update) {
      window.liquidAjaxCart.update({ updates: updates }, {
        lastCallback: function () { if (callback) callback(); }
      });
    } else {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function () { if (callback) callback(); })
        .catch(function () { if (callback) callback(); });
    }
    return true;
  }

  // Keep OT005's membership quantity equal to its board quantity.
  //
  // Same failure syncOt001ProgramQty guards against, but the pairing is looser:
  // the two Reformly+ subscriptions are not tied to a board VARIANT the way an
  // OT001 programme is to its plan, because the cycle is chosen in the offer
  // card, not as a product option. So the target is on the TOTAL across both
  // ids, not per id.
  //
  // The case that reported this: two boards added, one Annual and one Flexible,
  // then the board line dropped 2 -> 1. Board qty 1, membership total 2, and
  // both subscriptions kept billing.
  //
  // Trims from the LAST cart line backwards, so the earliest-added membership
  // survives. Deterministic, and matches "first choice wins".
  //
  // Only trims down. A board qty INCREASE is matched only in the unambiguous
  // single-membership case: with both cycles present there is no way to know
  // which the extra board should carry, and silently adding a recurring charge
  // is the worse error. The buyer adds the second from the page.
  function syncOt005MembershipQty(cart, callback) {
    var boardQty = 0;
    var lines = [];
    var total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (OT005_BOARDS.indexOf(item.variant_id) !== -1) boardQty += item.quantity;
      if (OT005_MEMBERSHIP_VARIANT_IDS.indexOf(item.variant_id) !== -1) {
        lines.push(item);
        total += item.quantity;
      }
    }

    // Zero board qty is the bundle sweep's job — it removes both ids outright.
    // Bailing here stops the two rules fighting over the same lines each sync.
    if (!lines.length || boardQty === 0 || total === boardQty) return false;

    var updates = {};
    if (total < boardQty) {
      if (lines.length !== 1) return false;
      updates[String(lines[0].variant_id)] = boardQty;
    } else {
      var excess = total - boardQty;
      for (var j = lines.length - 1; j >= 0 && excess > 0; j--) {
        var cut = Math.min(excess, lines[j].quantity);
        updates[String(lines[j].variant_id)] = lines[j].quantity - cut;
        excess -= cut;
      }
    }

    if (window.liquidAjaxCart && window.liquidAjaxCart.update) {
      window.liquidAjaxCart.update({ updates: updates }, {
        lastCallback: function () { if (callback) callback(); }
      });
    } else {
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates: updates })
      })
        .then(function () { if (callback) callback(); })
        .catch(function () { if (callback) callback(); });
    }
    return true;
  }

  function handleCartChange() {
    if (busy) return;
    // Skip all gift logic on membership page to prevent cart drawer opening
    if (document.querySelector('.rmh__page')) return;

    var cart =
      window.liquidAjaxCart && window.liquidAjaxCart.cart
        ? window.liquidAjaxCart.cart
        : null;

    if (!cart) return;

    // Always correct visible counts
    updateDomCounts(cart);

    // Program quantity must track its board's. Returns early while the update
    // is in flight; the resulting cart change re-enters here.
    if (syncOt001ProgramQty(cart, function () { busy = false; handleCartChange(); })) {
      busy = true;
      return;
    }

    if (syncOt005MembershipQty(cart, function () { busy = false; handleCartChange(); })) {
      busy = true;
      return;
    }

    var rxQty = getReformerXQty(cart);

    // Plan switched: bin the previous plan's digital product first, so the
    // add below cannot briefly leave two program gifts in the cart.
    var stale = getStaleGiftItems(cart);
    if (stale.length > 0) {
      busy = true;
      updateGiftQty(stale, 0, function () {
        busy = false;
        handleCartChange();
      });
      return;
    }

    var giftItems = getGiftItems(cart);
    var missing = getMissingGiftIds(cart);

    if (rxQty > 0 && missing.length > 0) {
      // ReformerX present but some gifts missing → add exactly 1 of each
      busy = true;
      addGifts(missing, 1, function () {
        busy = false;
        handleCartChange(); // re-check in case add was partial
      });
    } else if (rxQty > 0 && giftItems.length > 0) {
      // All gifts present — ensure quantity stays at 1
      var needsSync = false;
      for (var i = 0; i < giftItems.length; i++) {
        if (giftItems[i].quantity !== 1) {
          needsSync = true;
          break;
        }
      }
      if (needsSync) {
        busy = true;
        updateGiftQty(giftItems, 1, function () {
          busy = false;
          handleCartChange(); // re-check in case update was partial
        });
      }
    } else if (rxQty === 0 && giftItems.length > 0) {
      // No qualifying products → remove hidden gifts
      busy = true;
      updateGiftQty(giftItems, 0, function () {
        busy = false;
        handleCartChange();
      });
    }

    // Separately: remove bundle gifts per board group
    if (!busy) {
      var oldBoardQty = 0;
      var summerBoardQty = 0;
      var july4YearlyBoardQty = 0;
      var july4MonthlyBoardQty = 0;
      var bonusBoardQty = 0;
      var r30BoardQty = 0;
      var r90BoardQty = 0;
      var nutritionGiftBoardQty = 0;
      var ot004BoardQty = 0;
      var ot005BoardQty = 0;
      var ot006BoardQty = 0;
      var ot006PlanQty = 0;
      var ot001PlanQty = {};
      for (var p = 0; p < cart.items.length; p++) {
        if (OLD_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          oldBoardQty += cart.items[p].quantity;
        }
        if (SUMMER_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          summerBoardQty += cart.items[p].quantity;
        }
        if (JULY4_YEARLY_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          july4YearlyBoardQty += cart.items[p].quantity;
        }
        if (JULY4_MONTHLY_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          july4MonthlyBoardQty += cart.items[p].quantity;
        }
        if (BONUS_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          bonusBoardQty += cart.items[p].quantity;
        }
        if (R30_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          r30BoardQty += cart.items[p].quantity;
        }
        if (NUTRITION_GIFT_BOARDS.indexOf(cart.items[p].variant_id) !== -1) {
          nutritionGiftBoardQty += cart.items[p].quantity;
        }
        if (OT004_GIFT_BOARDS.indexOf(cart.items[p].variant_id) !== -1) {
          ot004BoardQty += cart.items[p].quantity;
        }
        if (OT005_BOARDS.indexOf(cart.items[p].variant_id) !== -1) {
          ot005BoardQty += cart.items[p].quantity;
        }
        if (OT006_BUNDLE_BOARDS.indexOf(cart.items[p].variant_id) !== -1) {
          ot006BoardQty += cart.items[p].quantity;
        }
        if (OT006_PLAN_BOARDS.indexOf(cart.items[p].variant_id) !== -1) {
          ot006PlanQty += cart.items[p].quantity;
        }
        if (R90_BOARD_HANDLES.indexOf(cart.items[p].handle) !== -1) {
          r90BoardQty += cart.items[p].quantity;
        }
        for (var op = 0; op < OT001_PLANS.length; op++) {
          if (OT001_PLANS[op].boards.indexOf(cart.items[p].variant_id) !== -1) {
            ot001PlanQty[OT001_PLANS[op].program] =
              (ot001PlanQty[OT001_PLANS[op].program] || 0) + cart.items[p].quantity;
          }
        }
      }

      /* A page that sells a membership standalone must not have it swept as an
         orphan. `.rmh__page` covers the four reformly-membership-hero* pages;
         [data-membership-standalone] is the opt-in marker for any other, and
         is what app-membership-002/003 use — they render reformly-am002-hero,
         which has no .rmh__page, so the 90-Day membership was being deleted
         the moment it was added. */
      var isMembershipPage = !!document.querySelector('.rmh__page, [data-membership-standalone]');
      var bundleRemovals = [];
      for (var b = 0; b < cart.items.length; b++) {
        var vid = cart.items[b].variant_id;
        if (!isMembershipPage && oldBoardQty === 0 && summerBoardQty === 0 && july4MonthlyBoardQty === 0 && vid === OLD_MEMBERSHIP_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        if (!isMembershipPage && july4YearlyBoardQty === 0 && r30BoardQty === 0 && vid === JULY4_YEARLY_MEMBERSHIP_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        if (!isMembershipPage && r30BoardQty === 0 && vid === R30_MEMBERSHIP_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        if (!isMembershipPage && r90BoardQty === 0 && vid === R90_MEMBERSHIP_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        if (!isMembershipPage && oldBoardQty === 0 && summerBoardQty === 0 && july4MonthlyBoardQty === 0 && r30BoardQty === 0 && vid === JULY4_MONTHLY_MEMBERSHIP_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        if (!isMembershipPage && ot005BoardQty === 0 && OT005_MEMBERSHIP_VARIANT_IDS.indexOf(vid) !== -1) {
          bundleRemovals.push(cart.items[b]);
        }
        // ot004BoardQty guard, exactly like the nutrition one below: OT004 hands
        // the socks out as one of its three card gifts, so without this the
        // bundle sweep would delete them the moment they were added and the two
        // rules would fight on every sync. NOT nutritionGiftBoardQty — that also
        // counts OT003's boards, and OT003 does not grant socks.
        if (summerBoardQty === 0 && july4YearlyBoardQty === 0 && july4MonthlyBoardQty === 0 && bonusBoardQty === 0 && r30BoardQty === 0 && r90BoardQty === 0 && ot004BoardQty === 0 && ot005BoardQty === 0 && vid === SUMMER_SOCKS_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        // nutritionGiftBoardQty guard: OT003 hands out the nutrition plan as one of
        // its gifts, so without this the bundle sweep would delete it the moment
        // it was added and the two rules would fight each other on every sync.
        if (july4YearlyBoardQty === 0 && r30BoardQty === 0 && r90BoardQty === 0 && nutritionGiftBoardQty === 0 && (vid === R30_NUTRITION_VARIANT_ID || vid === R30_SHIPPING_VARIANT_ID)) {
          bundleRemovals.push(cart.items[b]);
        }
        /* ot006BoardQty guard. OT006 now ships its OWN duplicate socks
           (46462330241081), so it no longer collides with this legacy variant —
           but the guard stays: it costs nothing, and it is what stopped the
           legacy sweep deleting the socks the moment they were added while the
           two shared one variant. Remove only if OT006 is retired. */
        if (oldBoardQty === 0 && july4YearlyBoardQty === 0 && july4MonthlyBoardQty === 0 && ot006BoardQty === 0 && vid === OLD_SOCKS_VARIANT_ID) {
          bundleRemovals.push(cart.items[b]);
        }
        /* OT006's bundle items go with the board that earned them, PER COLOUR.
           Matched on the _ot006_gift property rather than on variant id alone:
           Pilates Mat and Weight Bands are ordinary products someone can buy on
           their own, and deleting by id would remove one the customer paid for.

           The line names its parent board variant, so the rule is simply "is
           that board still in the cart" — no pairing table to maintain here, and
           nothing to keep in step with the snippet that does the adding. Remove
           the Black bundle and Black's three lines go; Pink's stay. */
        if (cart.items[b].properties && cart.items[b].properties._ot006_gift) {
          var parentId = String(cart.items[b].properties._ot006_gift);
          var parentPresent = false;
          for (var pb = 0; pb < cart.items.length; pb++) {
            if (String(cart.items[pb].variant_id) === parentId) { parentPresent = true; break; }
          }
          if (!parentPresent) bundleRemovals.push(cart.items[b]);
        }
        /* The programme goes with the board that bought it, matched the same way
           as the gifts: the line names its parent in _ot006_plan.

           A count-based rule ("no With Plan board left") was not enough — with
           two bundles in the cart, removing one left the other's count above
           zero and BOTH subscriptions survived, billing twice. The property also
           stops the two lines merging into one at quantity 2, since Shopify keys
           a line on variant plus properties. */
        if (cart.items[b].properties && cart.items[b].properties._ot006_plan) {
          var planParent = String(cart.items[b].properties._ot006_plan);
          var planParentPresent = false;
          for (var pp = 0; pp < cart.items.length; pp++) {
            if (String(cart.items[pp].variant_id) === planParent) { planParentPresent = true; break; }
          }
          if (!planParentPresent) bundleRemovals.push(cart.items[b]);
        }

        /* Belt and braces for carts created BEFORE the property existed: those
           programme lines carry no parent, so fall back to the count rule. */
        if (ot006PlanQty === 0 && OT006_PROGRAM_VARIANTS.indexOf(vid) !== -1 &&
            !(cart.items[b].properties && cart.items[b].properties._ot006_plan)) {
          bundleRemovals.push(cart.items[b]);
        }
        // OT001: a program goes the moment ITS OWN plan's board leaves, even if
        // a board on a different plan is still in the cart. No membership-page
        // guard — there is no page that sells these variants standalone.
        for (var oq = 0; oq < OT001_PLANS.length; oq++) {
          if (vid === OT001_PLANS[oq].program && !ot001PlanQty[OT001_PLANS[oq].program]) {
            bundleRemovals.push(cart.items[b]);
            break;
          }
        }
      }

      if (bundleRemovals.length > 0) {
        busy = true;
        // Line-precise: two OT006 socks lines share a variant, so the id-keyed
        // update would zero both when only one board was removed.
        updateLinesQty(cart, bundleRemovals, 0, function () {
          busy = false;
          handleCartChange();
        });
        return;
      }

      /* Keep each OT006 gift and programme line at the SAME quantity as the
         board that earned it.

         Was a hard clamp to 1, which was right only while lines could merge
         across colours — two bundles both adding the colourless Grip Socks
         collapsed into one line at quantity 2. Parent ids fixed that, and the
         clamp then became wrong in the other direction: adding the same bundle
         twice legitimately gives quantity 2, and the clamp kept deleting one of
         each.

         Syncing to the parent's quantity covers both. A line whose parent is
         gone is not touched here — the sweep above removes it outright. */
      var boardQtyByVariant = {};
      for (var bq = 0; bq < cart.items.length; bq++) {
        var bqId = String(cart.items[bq].variant_id);
        boardQtyByVariant[bqId] = (boardQtyByVariant[bqId] || 0) + cart.items[bq].quantity;
      }

      var qtyMismatch = [];
      var qtyTargets = [];
      for (var gq = 0; gq < cart.items.length; gq++) {
        var gItem = cart.items[gq];
        if (!gItem.properties) continue;
        var parent = gItem.properties._ot006_gift || gItem.properties._ot006_plan;
        if (!parent) continue;
        var want = boardQtyByVariant[String(parent)];
        if (want && gItem.quantity !== want) {
          qtyMismatch.push(gItem);
          qtyTargets.push(want);
        }
      }
      if (qtyMismatch.length > 0) {
        busy = true;
        updateLinesQtyEach(cart, qtyMismatch, qtyTargets, function () {
          busy = false;
          handleCartChange();
        });
      }
    }
  }

  /* ── event binding ──────────────────────────────────────── */

  function init() {
    document.addEventListener('liquid-ajax-cart:request-end', function (event) {
      handleCartChange();

      // Track summer sale board add-to-cart in PostHog
      if (
        window.posthog &&
        event.detail &&
        event.detail.requestState &&
        event.detail.requestState.requestType === 'add'
      ) {
        var cart = window.liquidAjaxCart && window.liquidAjaxCart.cart;
        if (cart) {
          for (var t = 0; t < cart.items.length; t++) {
            if (SUMMER_BOARD_HANDLES.indexOf(cart.items[t].handle) !== -1) {
              window.posthog.capture('summer_sale_add_to_cart', {
                product_title: cart.items[t].title,
                variant_title: cart.items[t].variant_title,
                variant_id: cart.items[t].variant_id,
                handle: cart.items[t].handle,
                price: cart.items[t].price
              });
              break; // one event per add action
            }
          }
        }
      }
    });

    // Handle bfcache restore (browser back from checkout)
    window.addEventListener('pageshow', function (event) {
      if (event.persisted) {
        // Page restored from bfcache — cart state may be stale, re-fetch and correct
        fetch('/cart.js')
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            updateDomCounts(cart);
          })
          .catch(function () { });
      }
    });

    /* ---- Batch the gifts into the board's own /cart/add.js ----

       Without this, adding a board costs two sequential round-trips before the
       drawer is correct:

         1. the product form adds the board
         2. request-end fires, handleCartChange() sees gifts missing, adds them

       Each of those re-renders the cart drawer section server-side (the maison
       drawer is ~160 KB of Liquid), and they cannot overlap — step 2 does not
       start until step 1's render has come back. That gap is the delay between
       clicking Add to cart and the drawer showing its gifts.

       Posting the board and the gifts as one items[] array collapses it to a
       single round-trip. handleCartChange() still runs afterwards and finds
       nothing missing, so it issues no request. This mirrors what OT005 already
       does via window.reformlyOt005GiftIds.

       Falls through to the normal submit on anything unexpected — a wrong guess
       here must never leave the button dead. */
    function bindBatchedBoardAdd() {
      document.addEventListener('submit', function (e) {
        var form = e.target;
        if (!form || form.tagName !== 'FORM') return;
        var action = form.getAttribute('action') || '';
        if (action.indexOf('/cart/add') === -1) return;
        if (!window.liquidAjaxCart || !window.liquidAjaxCart.add) return;

        /* Only qualifying boards get gifts, and only this page's product can be
           identified at submit time — the cart does not have the line yet. */
        var path = window.location.pathname;
        var qualifies = false;
        for (var i = 0; i < FREE_GIFT_HANDLES.length; i++) {
          if (path.indexOf('/products/' + FREE_GIFT_HANDLES[i]) !== -1) { qualifies = true; break; }
        }
        if (!qualifies) return;

        var cart = (window.liquidAjaxCart && window.liquidAjaxCart.cart) || null;
        if (!cart) return;

        /* Already reconciled for a board in the cart — nothing to batch. */
        var giftIds = effectiveGiftIds(cart);
        var missing = getMissingGiftIds(cart);
        if (!missing.length) return;

        var fd = new FormData(form);
        var id = fd.get('id');
        if (!id) return;

        e.preventDefault();
        e.stopPropagation();

        var board = { id: Number(id), quantity: Number(fd.get('quantity') || 1) };
        if (fd.get('selling_plan')) board.selling_plan = Number(fd.get('selling_plan'));

        var props = {};
        fd.forEach(function (v, k) {
          var m = k.match(/^properties\[(.+)\]$/);
          if (m && v !== '') props[m[1]] = v;
        });
        for (var pk in props) { board.properties = props; break; }

        var items = [board];
        for (var g = 0; g < missing.length; g++) {
          items.push({ id: missing[g], quantity: 1, properties: { _free_gift: 'true' } });
        }

        /* Loading state, by hand.

           preventDefault + stopPropagation above means the theme's product-form
           handler never sees this submit, and that handler is what normally sets
           the button's loading state (product-form.js:129) — so without this the
           button sat inert for the whole request with no spinner.

           Same class and spinner element the theme uses, so the existing CSS and
           the #AddToCart `loading` watcher both behave exactly as before. */
        var submitBtn = form.querySelector('[type="submit"]') ||
          form.querySelector('.product-form__submit');
        var spinner = form.querySelector('.loading__spinner');
        if (submitBtn) {
          submitBtn.setAttribute('aria-disabled', true);
          submitBtn.classList.add('loading');
        }
        if (spinner) spinner.classList.remove('hidden');

        var clearLoading = function () {
          document.documentElement.classList.remove('js-cart-adding');
          if (submitBtn) {
            submitBtn.removeAttribute('aria-disabled');
            submitBtn.classList.remove('loading');
          }
          if (spinner) spinner.classList.add('hidden');
        };

        /* Raw fetch, NOT liquidAjaxCart.add().

           The library injects `sections` into every add, so the response carries
           a full server render of the cart drawer — 225 kB, 2.56s on Fast 4G.
           It then throws that HTML away: after an add it unconditionally
           re-fetches sections anyway (`if (requestType === "add")
           m.push(...p.slice(0,5))` in liquid-ajax-cart-v2.1.2.js). So the render
           in the add response is paid for and never read.

           Going direct keeps the add at ~2 kB. The drawer is then rendered once
           by the update() below — the same single render the library would have
           done — and the button's loading state clears as soon as the cart
           mutation lands rather than after a render nobody uses. That clearing
           is what dispatches instant:add-to-cart and opens the drawer, so the
           drawer appears ~2s earlier.

           busy blocks handleCartChange() from firing a duplicate add off the
           update()'s request-end. */
        /* Marks an ADD in progress, for the drawer CSS.

           The drawer opens before the render lands, so its DOM is still the
           pre-add one — on a first add that is the empty-cart state, which
           would flash "your cart is empty" right after someone added. The CSS
           hides it while this class is set.

           A dedicated class, not liquid-ajax-cart's js-ajax-cart-processing:
           that one is on for every request including REMOVALS, and suppressing
           the empty state during a removal delays the legitimate "cart is
           empty" until the hidden subscription line finishes being removed
           too — a full extra request behind live behaviour. */
        document.documentElement.classList.add('js-cart-adding');

        busy = true;
        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ items: items })
        })
          .then(function (r) { return r.json(); })
          .then(function () {
            /* Clear BEFORE the render request: this is the whole point — the
               button must not stay spinning through the drawer render. */
            clearLoading();

            /* Sync the header badge straight away, off /cart.js.

               The badge is written ONLY by updateDomCounts(), which sits inside
               handleCartChange() behind `if (busy) return` — and liquid-ajax-cart
               never re-renders the header (only the section registered with
               data-ajax-cart-section, i.e. the drawer). So if the reconciler is
               blocked or its callback never lands, the nav count stays stale
               until a page load. That is the reported desync.

               /cart.js is ~300 bytes and independent of the drawer render, so the
               count corrects in a few hundred ms regardless of what the
               reconciler does afterwards. It runs again via handleCartChange()
               once the gifts settle, which is harmless — same value. */
            fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
              .then(function (r) { return r.json(); })
              .then(function (freshCart) { updateDomCounts(freshCart); })
              .catch(function () { });

            /* Watchdog on `busy`.

               handleCartChange() — which is what writes the header cart count
               via updateDomCounts() — starts with `if (busy) return`. If the
               lastCallback below never fires (a dropped or superseded request,
               or a listener throwing earlier in the chain) then busy stays true
               for the life of the page, every later cart change is ignored, and
               the count only corrects on a reload. Reported as the cart icon
               going out of sync until refreshed.

               8s is far longer than a normal render (~1.5s), so this only ever
               fires when something genuinely went wrong. */
            var settled = false;
            var settle = function () {
              if (settled) return;
              settled = true;
              busy = false;
              handleCartChange();
            };
            setTimeout(settle, 8000);

            window.liquidAjaxCart.update({}, { lastCallback: settle });
          })
          .catch(function () {
            /* Never leave the button stuck on a failed add. */
            clearLoading();
            busy = false;
          });
      }, true);
    }

    bindBatchedBoardAdd();

    // Run once on init to catch page-load state
    handleCartChange();

    // Page view tracking for summer sale products
    if (window.posthog) {
      var path = window.location.pathname;
      for (var s = 0; s < SUMMER_BOARD_HANDLES.length; s++) {
        if (path.indexOf(SUMMER_BOARD_HANDLES[s]) !== -1) {
          window.posthog.capture('summer_sale_page_view', {
            handle: SUMMER_BOARD_HANDLES[s],
            url: window.location.href,
            referrer: document.referrer
          });
          break;
        }
      }
    }
  }

  if (window.liquidAjaxCart && window.liquidAjaxCart.init) {
    init();
  } else {
    document.addEventListener('liquid-ajax-cart:init', function () {
      init();
    });
  }
})();
